import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _class, _ProgressManagerPopup;
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupPosition } from "abreact/incubator/ui/popup/PopupPosition";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbButton } from "abreact/misc/AbButton";
import { AbSize } from "abreact/misc/AbSize";
import { AbEllipsisAnimation } from "abreact/progress/AbEllipsisAnimation";
import { AbProgress } from "abreact/progress/AbProgress";
import { AbProgressIndicator } from "abreact/progress/AbProgressIndicator";
import { BlockingProgressAction } from "abreact/progress/manager/BlockingProgressAction";
import { ProgressManager } from "abreact/progress/manager/ProgressManager";
import { DebugState } from "abweb/service/debug/DebugState";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { DateUtils } from "abweb/utils/DateUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { observer } from "mobx-react";
import * as React from "react";
export let ProgressManagerPopup = observer(_class = (_ProgressManagerPopup = class ProgressManagerPopup extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "stateTimerId", undefined);
    _defineProperty(this, "renderTimerId", undefined);
    _defineProperty(this, "alertMessageId", TypeUtils.generateUniqueId("ab-progress-manager"));
    const displayDelay = props.displayDelay ?? 0;
    this.state = {
      status: displayDelay > 0 ? "delayed" : "displayed"
    };
  }
  render() {
    if (this.renderTimerId !== undefined) {
      clearTimeout(this.renderTimerId);
      this.renderTimerId = undefined;
    }
    const seconds = Math.floor((new Date().getTime() - this.props.startOfTime.getTime()) / 1000);
    const elapsedTime = AbReactResources.progressManager_elapsedTime(DateUtils.toHHMMSS(seconds));
    const now = Date.now();
    let nextTimeToRun = Number.MAX_SAFE_INTEGER;
    const taskList = ProgressManager.singleton.taskList;
    if (taskList == null) {
      ProgressManager.hideGlobalSpinney();
      return null;
    }
    const runningTasks = taskList.filter(task => task.showing);
    const blockingTasks = runningTasks.filter(ProgressManagerPopup.isBlocking);
    const cancellableTasks = blockingTasks.filter(task => task.onCancel);
    const anythingRunning = runningTasks.length > 0;
    const anythingBlocking = blockingTasks.length > 0;
    const stackOfProgress = blockingTasks.map((task, i) => {
      let indicator;
      if (task.report) {
        const {
          current,
          currentText
        } = task.report(task.name);
        let pct = current; // current is a percentage
        if (task.steps != null) {
          pct = current * 100 / (task.steps - 1);
        } // current is a step number between 0 and steps - 1.
        pct = NumberUtils.clamp(pct, 0, 100); // pin between 0 and 100
        indicator = /*#__PURE__*/React.createElement(AbProgressIndicator, {
          message: currentText,
          pctComplete: pct
        });
        if (task.timeToRun <= now) {
          // either this task was never displayed or was displayed in the past.  Move its next time into the future.
          task.timeToRun = now + (task.reportInterval != null ? task.reportInterval : BlockingProgressAction.DEFAULT_REPORT_INTERVAL);
        }
        nextTimeToRun = Math.min(nextTimeToRun, task.timeToRun);
      } else if (i === 0) {
        indicator = /*#__PURE__*/React.createElement(AbEllipsisAnimation, null);
      }
      const progressMessage = task.cancelling ? AbReactResources.progressManager_cancelling() : task.title;
      return /*#__PURE__*/React.createElement("div", {
        id: this.alertMessageId,
        className: ReactUtils.classNamesString(["ab-progress-manager-stack-item", task.report && "ab-progress-manager-bar-item"]),
        key: i
      }, !task.report && indicator, DebugState.instance.isEnabled && /*#__PURE__*/React.createElement("div", {
        className: "ab-progress-manager-title"
      }, task.name + ":"), task.title && /*#__PURE__*/React.createElement("div", {
        className: "ab-progress-manager-title"
      }, AbProgress.renderProgressContent(progressMessage)), task.report && indicator, task.onCancel !== undefined && /*#__PURE__*/React.createElement(AbButton, {
        className: "ab-progress-manager-bottom-button",
        size: AbSize.EXTRA_SMALL,
        label: AbReactResources.abCancelButton_label(),
        disabled: task.cancelling,
        onClick: () => {
          task.onCancel?.(task.name).then(() => task.hideUI());
        }
      }));
    });
    if (!anythingRunning) {
      // hide the global spinney
      ProgressManager.hideGlobalSpinney();
      return null;
    }
    if (!anythingBlocking) {
      // It's up to the client to show a local spinney, but we show the global spinney.
      ProgressManager.showGlobalSpinney();
      return null;
    }

    // Call ourselves back when it's time to run again.  Probably a second from now.
    Assert.assert(nextTimeToRun - now > 0, "ProgressManager: next time isn't in the future");
    this.renderTimerId = setTimeout(() => this.forceUpdate(), nextTimeToRun - now);

    // We have something blocking.  Show it.
    // See https://www.w3.org/WAI/ARIA/apg/patterns/alertdialog/ for the aria and role explanation.
    return /*#__PURE__*/React.createElement(AbPopup, {
      className: StringUtils.cssClassStr("ab-progress-manager-popup", this.state.status),
      overlayClassnames: StringUtils.cssClassStr("ab-progress-manager-overlay", this.state.status),
      dataTestId: "ab-progress-manager-popup",
      position: PopupPosition.CENTER,
      draggable: false,
      overlay: true,
      transparentOverlay: false,
      onKeyDown: event => {
        if (event.key === "Escape" && cancellableTasks.length > 0) {
          // The escape key cancels all cancellable tasks
          cancellableTasks.forEach(task => {
            task.onCancel(task.name).then(() => task.hideUI());
          });
        }
        return true;
      },
      modal: true,
      focus: true,
      __htmlProps: {
        "aria-describedby": this.alertMessageId,
        "aria-label": AbReactResources.progressManager_ariaLabel()
      },
      role: "alertdialog"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ab-progress-manager"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ab-progress-manager-stack"
    }, stackOfProgress), ProgressManagerPopup.showElapsedTime && seconds > ProgressManager.ELAPSED_TIME_DELAY_SECONDS && /*#__PURE__*/React.createElement("div", {
      className: "ab-progress-manager-elapsed-time"
    }, elapsedTime)));
  }
  componentDidMount() {
    const displayDelay = this.props.displayDelay ?? 0;
    if (displayDelay > 0) {
      this.stateTimerId = setTimeout(() => this.setState({
        status: "displayed"
      }), displayDelay);
    }
  }
  componentWillUnmount() {
    // Can't call super.componentWillUnmount
    if (this.stateTimerId !== undefined) {
      clearTimeout(this.stateTimerId);
      this.stateTimerId = undefined;
    }
    if (this.renderTimerId !== undefined) {
      clearTimeout(this.renderTimerId);
      this.renderTimerId = undefined;
    }
  }
  static isBlocking(obj) {
    return ClassUtils.isInstanceOf(obj, BlockingProgressAction);
  }
}, _defineProperty(_ProgressManagerPopup, "showElapsedTime", false), _ProgressManagerPopup)) || _class;
//# sourceMappingURL=ProgressManagerPopup.js.map
